import {
  Badge,
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { MessageIcon } from "../../../../../assets/images/auth/message";
import {
  HelpIcon,
  PendingTask,
} from "../../../../../assets/common-svg/CommonSvg";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { LogoutIcon } from "../../../../../assets/images/auth/logoutIcon";
import { useSocketInstance } from "../../../../../context/SocketContext";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "../../../../utils/methods/notification";
import InitialsAvatar from "../../../common/InitialsAvatar";
import { NotificationIcon } from "../../../../assets/images/icons/notification.icon";
import AccordianModal from "../../../../pages/Master/help/accordianModal";
import {
  viewHelpBySlug,
  viewHelpDetailsBySlugs,
  clearSubjectDataList
} from "../../../../slices/help";

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { messageRead, notifiactioCount, taskManager, socketInstance } =
    useSocketInstance();
  const userData = useSelector((state) => state.loginData);
   const portal_access = userData?.portal_access;
  const role_id = userData?.role_id;
  const [anchorEl, setAnchorEl] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const open = Boolean(anchorEl);
  const viewHelpData = useSelector(viewHelpDetailsBySlugs);
  let slug = useSelector((state) => state.bmrHelp.storeSlugs);
  const dispatch = useDispatch();

  useEffect(() => {
    if (socketInstance) {
      socketInstance.emit("count_task", null);
    }
  }, []);

  const handleHelpModal = async () => {
    await dispatch(viewHelpBySlug(slug));
    setHelpOpen(true);
  };

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback(async () => {
    dispatch(clearSubjectDataList())
    localStorage.clear();
    handleClose();
    notify.success("Logout successfully");
    setTimeout(() => {
      navigate("/login");
    }, 500);
  }, [navigate, handleClose]);

  const currentPhase = useMemo(() => {
    if (location.pathname.includes("/bmr/")) {
      return "bmr";
    } else if (location.pathname.includes("/cdm/")) {
      return "cdm";
    }
    return "bmr";
  }, [location.pathname]);

  const pendingTaskRoute = useMemo(() => {
    return currentPhase === "bmr" ? "/bmr/pending-tasks" : "/tasks";
  }, [currentPhase]);

  // Updated: Different routes but same data source
  const changeRequestRoute = useMemo(() => {
    return currentPhase === "bmr" ? "/bmr/change-requests" : "/change-requests";
  }, [currentPhase]);

  // Add this function to handle change request navigation with data consistency
  const handleChangeRequestNavigation = useCallback(
    (e) => {
      e.preventDefault();
      // Navigate to the appropriate route but ensure it uses CDM data
      navigate(changeRequestRoute, {
        state: {
          useDataSource: "cdm", // Pass a flag to indicate which data source to use
          originalPhase: currentPhase,
        },
      });
    },
    [navigate, changeRequestRoute, currentPhase]
  );

  // Memoize menu props
  const menuProps = useMemo(
    () => ({
      anchorEl,
      id: "account-menu",
      open,
      onClose: handleClose,
      onClick: handleClose,
      className: "p-[10px] capitalize",
      slotProps: {
        paper: {
          elevation: 0,
          sx: {
            width: 220,
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        },
      },
      transformOrigin: { horizontal: "right", vertical: "top" },
      anchorOrigin: { horizontal: "right", vertical: "bottom" },
    }),
    [anchorEl, open, handleClose]
  );

  // Memoize notification badge content
  const notificationBadgeContent = useMemo(
    () => (notifiactioCount >= 100 ? "99+" : notifiactioCount),
    [notifiactioCount]
  );

  return (
    <Box
      sx={{
        flexShrink: 0,
        marginLeft: "auto",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Link className="p-[10px]" onClick={handleHelpModal}>
        <Tooltip title="FAQ">
          <Badge>
            <HelpIcon />
          </Badge>
        </Tooltip>
      </Link>
      {role_id != 1 && (
        <Link to={pendingTaskRoute} className="p-[10px]">
          <Tooltip title="Tasks">
            <Badge
              invisible={!taskManager}
              badgeContent={taskManager}
              color="error"
            >
              <PendingTask />
            </Badge>
          </Tooltip>
        </Link>
      )}
      {role_id === 1 && (
        <Link
          to={changeRequestRoute}
          className="p-[10px]"
          onClick={handleChangeRequestNavigation}
        >
          <Tooltip title="Change Request">
            <Badge
              invisible={!taskManager}
              badgeContent={taskManager}
              color="error"
            >
              <PendingTask />
            </Badge>
          </Tooltip>
        </Link>
      )}
      <Link to={portal_access  == '2' ? "/bmr/message" : "/message"} className="p-[10px]">
        <Tooltip title="Message">
          <Badge invisible={!messageRead} badgeContent=" " color="error">
            <MessageIcon />
          </Badge>
        </Tooltip>
      </Link>
      <Link to="/bmr/notifications" className="p-[10px]">
        <Tooltip title="Notifications">
          <Badge
            invisible={!notifiactioCount}
            badgeContent={notificationBadgeContent}
            color="error"
          >
            <NotificationIcon />
          </Badge>
        </Tooltip>
      </Link>
      <ButtonBase
        onClick={handleClick}
        className="p-[6px] gap-2 ml-2 rounded-sm capitalize"
        aria-controls={open ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <b>{userData?.name}</b>
        <InitialsAvatar>{userData?.name}</InitialsAvatar>
      </ButtonBase>
      <Menu {...menuProps}>
        <MenuItem>
          <Stack
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
            className="w-100"
          >
            <InitialsAvatar size={66} fontSize={20}>
              {userData?.name}
            </InitialsAvatar>
            <p className="font-bold text-gray-700 mt-1.5 font-[roboto] ">
              {userData?.name}
            </p>
            <p className="text-xs text-gray-500 font-[roboto]">
              {userData?.email}
            </p>
          </Stack>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <p className="text-base text-[#494949] font-[roboto]">Logout</p>
        </MenuItem>
      </Menu>
      <AccordianModal
        data={viewHelpData}
        open={helpOpen}
        setOpen={setHelpOpen}
        ModalTitle={"Frequently Asked Questions "}
      />
    </Box>
  );
};

export default memo(Profile);
